import { friendlyApiErrorMessage } from 'src/app/services/api-error-notifier.service';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { AgGridModule } from 'ag-grid-angular';
import { ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';
import moment from 'moment';
import { catchError, of, Subscription } from 'rxjs';
import { ApiService } from 'src/app/services/api.service';
import { FilterService } from 'src/app/services/filter.service';
import { ProgressIndefinite } from '../../progress-indicator/indefinite';
import {
  BasicPopupGridIconCellComponent,
  ConditionalFormat,
} from './basic-popup-grid-icon-cell.component';

export type { ConditionalFormat } from './basic-popup-grid-icon-cell.component';

/** Column definition accepted by the popup. Extends AG Grid's `ColDef` with
 *  two convenience fields that the popup translates into native AG Grid
 *  behaviour before handing the columnDefs to the grid:
 *
 *    - `textAlign` → `cellClass` + `headerClass` (`bpg-align-{left|center|right}`)
 *    - `format`    → `valueFormatter` (locale number / moment date / passthrough)
 *
 *  Both fields are optional; defaults are `textAlign: 'left'`, `format: 'string'`.
 *  Any other `ColDef` option (cellRenderer, sortable, width, …) still works. */
export interface BasicPopupGridColDef extends ColDef {
  /** Horizontal alignment applied to both header and cell. Defaults to 'left'. */
  textAlign?: 'left' | 'center' | 'right';
  /** Value-formatter type. 'string' (default) → raw value, 'number' →
   *  `toLocaleString()` (commas, etc.), 'date' → `moment(value).format(dateFormat)`. */
  format?: 'string' | 'number' | 'date';
  /** Date mask used only when `format === 'date'`. Defaults to 'DD MMM YYYY'. */
  dateFormat?: string;
  /** Conditional formatting — when set, renders an `mat-icon` (registered
   *  with MatIconRegistry) to the left of the cell value, coloured per the
   *  matching rule. Match key is the cell value (or `compareField` from the
   *  row, if supplied), normalised with `trim()` + `toUpperCase()`. */
  conditionalFormat?: ConditionalFormat[];
  /** When `conditionalFormat` is set, compare a DIFFERENT field from the
   *  row to choose the icon (e.g. display `status_group` but match against
   *  `case_status_group`). Defaults to the column's own `field`. */
  compareField?: string;
}

/**
 * Input contract for `BasicPopupGridComponent`. Open via:
 *
 *   this.dialog.open(BasicPopupGridComponent, {
 *     data: <BasicPopupGridDialogData>{ heading: 'Patient List', apiKey: 123, ... },
 *     panelClass: 'basic-popup-grid-dialog'
 *   });
 */
export interface BasicPopupGridDialogData {
  /** Header text shown in the popup title bar. Rendered in uppercase. */
  heading?: string;
  /** Pre-fetched rows to render directly. When provided, the popup skips the
   *  API call entirely and shows these rows — use this when the caller already
   *  has the data in memory. `apiKey`/`endpoint`/`payload` are then ignored. */
  rowData?: any[];
  /** Backend `api_key` posted with the data fetch. Required unless `rowData`
   *  is provided (local-data mode). */
  apiKey?: string | number;
  /** Optional endpoint override; defaults to `apiService.executionUrl`. */
  endpoint?: string;
  /** Extra fields merged into the POST body alongside `filterService.baseQuery`. */
  payload?: Record<string, any>;
  /** `report_typ` override; defaults to `filterService.report_type` (or 'D'). */
  reportType?: string;
  /** AG Grid column definitions, with the optional `textAlign`, `format`,
   *  and `dateFormat` extensions handled by the popup. When omitted, columns
   *  are auto-derived from the first response row's keys. */
  columnDefs?: BasicPopupGridColDef[];
  /** AG Grid `defaultColDef` overrides — merged on top of sensible defaults. */
  defaultColDef?: ColDef;
  /** Initial pagination page size. Defaults to 15 (matches the design mock). */
  pageSize?: number;
  /** Excel/CSV export filename (without extension). Defaults to the heading. */
  exportFileName?: string;
}

@Component({
    imports: [
        CommonModule,
        FormsModule,
        MatDialogModule,
        MatIconModule,
        MatMenuModule,
        AgGridModule,
        ProgressIndefinite,
    ],
    selector: 'app-basic-popup-grid',
    templateUrl: './basic-popup-grid.component.html',
    styleUrls: ['./basic-popup-grid.component.scss']
})
export class BasicPopupGridComponent implements OnInit, OnDestroy {
  isLoading = false;
  errorMessage = '';
  rowData: any[] = [];
  columnDefs: ColDef[] = [];
  defaultColDef: ColDef = {
    flex: 1,
    minWidth: 100,
    sortable: true,
    resizable: true,
    filter: true,
    /* Wrap long text and let rows auto-grow when content doesn't fit on
       one line. `rowHeight` (50) acts as a min-height in this mode. */
    wrapText: true,
    autoHeight: true,
  };

  /** Live search text bound to the header search input → ag-grid quick filter. */
  searchText = '';
  /** Currently applied page size. */
  paginationPageSize = 15;
  /** Pending page size shown in the header input; applied on Go click / Enter. */
  pendingPageSize = 15;
  /** Row height in px. Matches patient-grid-list (50). */
  rowHeight = 50;

  private gridApi: GridApi | null = null;
  /** Column API — used by the search's external filter to read the currently
   *  displayed columns (mirrors patient-grid-list). */
  private gridColumnApi: any = null;
  private sub: Subscription | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public dialogData: BasicPopupGridDialogData,
    private dialogRef: MatDialogRef<BasicPopupGridComponent>,
    private httpClient: HttpClient,
    private apiService: ApiService,
    private filterService: FilterService,
  ) {}

  get heading(): string {
    return this.dialogData?.heading || '';
  }

  get rowCount(): number {
    return this.rowData?.length || 0;
  }

  get exportFileName(): string {
    const base = (this.dialogData?.exportFileName || this.heading || 'export').trim();
    return base || 'export';
  }

  ngOnInit(): void {
    if (this.dialogData?.defaultColDef) {
      this.defaultColDef = { ...this.defaultColDef, ...this.dialogData.defaultColDef };
    }
    if (this.dialogData?.columnDefs?.length) {
      this.columnDefs = this.processColumnDefs(this.dialogData.columnDefs);
    }
    if (this.dialogData?.pageSize && this.dialogData.pageSize > 0) {
      this.paginationPageSize = this.dialogData.pageSize;
      this.pendingPageSize = this.dialogData.pageSize;
    }
    this.loadData();
  }

  onGridReady(event: GridReadyEvent): void {
    this.gridApi = event.api;
    this.gridColumnApi = (event as any).columnApi;
    if (this.searchText) {
      this.gridApi.onFilterChanged();
    }
  }

  /** Type-to-Search → contiguous substring match across the visible columns
   *  (same behaviour as patient-grid-list). Uses ag-grid's EXTERNAL filter
   *  rather than the built-in quick filter, whose whitespace-tokenised match
   *  treats "john 25" as two independent terms; the external filter only
   *  passes a row whose joined cell text CONTAINS the exact typed string. */
  onSearchChanged(value: string): void {
    this.searchText = value || '';
    if (this.gridApi) this.gridApi.onFilterChanged();
  }

  isExternalFilterPresent = (): boolean => {
    return !!this.searchText && String(this.searchText).trim().length > 0;
  };

  doesExternalFilterPass = (node: any): boolean => {
    if (!this.searchText || !node?.data) return true;
    const search = String(this.searchText).toLowerCase().trim();
    if (!search) return true;

    const cols = this.gridColumnApi?.getAllDisplayedColumns?.() || [];
    const rowText = cols
      .map((col: any) => {
        const field = col.getColDef?.()?.field;
        if (!field) return '';
        const v = node.data[field];
        return v == null ? '' : String(v);
      })
      .join(' ')
      .toLowerCase();

    return rowText.includes(search);
  };

  applyPageSize(): void {
    const next = Number(this.pendingPageSize);
    if (!Number.isFinite(next) || next <= 0) return;
    this.paginationPageSize = next;
    if (this.gridApi) this.gridApi.paginationSetPageSize(next);
  }

  exportExcel(): void {
    if (!this.gridApi) return;
    const fileName = this.exportFileName;
    try {
      // Available when ag-grid-enterprise is loaded (it is, app-wide).
      (this.gridApi as any).exportDataAsExcel?.({
        fileName,
        sheetName: this.filterService.selectedBrandName || fileName,
        processCellCallback: this.processCellForExport,
      });
    } catch {
      // Fall back to CSV if Excel export isn't available in the current build.
      this.exportCsv();
    }
  }

  exportCsv(): void {
    if (!this.gridApi) return;
    this.gridApi.exportDataAsCsv({
      fileName: this.exportFileName,
      processCellCallback: this.processCellForExport,
    });
  }

  /** Run each exported cell through the column's `valueFormatter` so the
   *  Excel / CSV output mirrors what the user sees in the grid — `format:
   *  'date'` columns export "20 May 2026" instead of the raw ISO string,
   *  `format: 'number'` columns export "1,234" with separators, plain
   *  string columns pass through unchanged. AG Grid exposes
   *  `params.formatValue` for this exact purpose; if it isn't available
   *  (older ag-grid versions), we fall back to calling the colDef's
   *  valueFormatter ourselves. Arrow-function form keeps `this` bound when
   *  AG Grid invokes the callback. */
  private processCellForExport = (params: any): any => {
    if (typeof params.formatValue === 'function') {
      return params.formatValue(params.value);
    }
    const colDef = params.column?.getColDef?.();
    const vf = colDef?.valueFormatter;
    if (typeof vf === 'function') {
      return vf({
        value: params.value,
        data: params.node?.data,
        node: params.node,
        column: params.column,
        colDef,
        api: params.api,
        columnApi: params.columnApi,
        context: params.context,
      });
    }
    return params.value;
  };

  close(): void {
    this.dialogRef.close();
  }

  private loadData(): void {
    // Local-data mode: caller passed the rows directly — render them and skip
    // the network round-trip entirely.
    if (Array.isArray(this.dialogData?.rowData)) {
      const rows = this.dialogData.rowData;
      this.rowData = rows;
      if ((!this.columnDefs || this.columnDefs.length === 0) && rows.length > 0) {
        const auto: BasicPopupGridColDef[] = Object.keys(rows[0]).map(k => ({
          field: k,
          headerName: this.toHeader(k),
        }));
        this.columnDefs = this.processColumnDefs(auto);
      }
      this.isLoading = false;
      return;
    }

    if (this.dialogData?.apiKey === undefined || this.dialogData?.apiKey === null || this.dialogData.apiKey === '') {
      this.errorMessage = 'Missing api_key for popup data fetch.';
      return;
    }

    if (this.sub) {
      this.sub.unsubscribe();
      this.sub = null;
    }

    this.isLoading = true;
    this.errorMessage = '';

    const body: any = {
      ...(this.filterService.baseQuery || {}),
      ...(this.dialogData.payload || {}),
      api_key: this.dialogData.apiKey,
      report_typ: this.dialogData.reportType || this.filterService.report_type || 'D',
      module_key: this.apiService.getModuleKey(),
    };

    const endpoint = this.dialogData.endpoint || this.apiService.executionUrl;
    const url = `${this.apiService.apiBaseUrl}/${endpoint}`;

    this.sub = this.httpClient
      .post<any>(url, body)
      .pipe(
        catchError((err: any) => {
          this.errorMessage =
            friendlyApiErrorMessage(err);
          return of([]);
        })
      )
      .subscribe((res: any) => {
        const rows: any[] = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
        this.rowData = rows;

        // Auto-derive columns from first row keys when the caller didn't
        // provide them — keeps the popup useful even before columnDefs are
        // wired up. Caller-provided columnDefs always win.
        if ((!this.columnDefs || this.columnDefs.length === 0) && rows.length > 0) {
          const auto: BasicPopupGridColDef[] = Object.keys(rows[0]).map(k => ({
            field: k,
            headerName: this.toHeader(k),
          }));
          this.columnDefs = this.processColumnDefs(auto);
        }

        this.isLoading = false;
      });
  }

  private toHeader(key: string): string {
    return key
      .replace(/[_-]+/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  }

  /** Translate the popup's extended column fields (`textAlign`, `format`,
   *  `dateFormat`) into native AG Grid props (`cellClass`, `headerClass`,
   *  `valueFormatter`), then strip the extensions so AG Grid doesn't warn
   *  about unknown keys. */
  private processColumnDefs(input: BasicPopupGridColDef[]): ColDef[] {
    return input.map(col => {
      const out: ColDef = { ...col };

      const align = (col.textAlign || 'left') as 'left' | 'center' | 'right';
      const alignClass = `bpg-align-${align}`;
      out.cellClass = this.appendClass(out.cellClass as any, alignClass);
      out.headerClass = this.appendClass(out.headerClass as any, alignClass);

      // Default the header tooltip to the headerName so users hovering the
      // (possibly truncated) header see the full label. CSS uppercases the
      // visual text — keep the tooltip in the caller's original casing.
      if (out.headerTooltip == null && out.headerName) {
        out.headerTooltip = out.headerName;
      }

      // When a column specifies an explicit `width`, opt it out of the
      // default `flex: 1` (set on defaultColDef) so the pixel width is
      // actually honoured — AG Grid ignores `width` whenever `flex` is set.
      if (out.width != null && out.flex == null) {
        out.flex = 0;
      }

      const format = col.format;
      if (format === 'number') {
        out.valueFormatter = (params: any) => {
          if (params.value === null || params.value === undefined || params.value === '') return '';
          const n = Number(params.value);
          return Number.isFinite(n) ? n.toLocaleString() : String(params.value);
        };
      } else if (format === 'date') {
        const fmt = col.dateFormat || 'MM/DD/YYYY';
        out.valueFormatter = (params: any) => {
          if (!params.value) return '';
          const m = moment(params.value);
          return m.isValid() ? m.format(fmt) : String(params.value);
        };
      }
      // 'string' (default) → no formatter; raw value is shown.

      // Conditional-format icon renderer: when rules are provided, switch
      // this column to the Angular cell renderer that prepends a coloured
      // <mat-icon> based on the matching rule.
      if (col.conditionalFormat && col.conditionalFormat.length > 0) {
        out.cellRenderer = BasicPopupGridIconCellComponent;
        out.cellRendererParams = {
          conditionalFormat: col.conditionalFormat,
          compareField: col.compareField,
        };
      }

      // Strip popup-only fields so AG Grid doesn't log "invalid colDef" warnings.
      delete (out as any).textAlign;
      delete (out as any).format;
      delete (out as any).dateFormat;
      delete (out as any).conditionalFormat;
      delete (out as any).compareField;
      return out;
    });
  }

  /** Append a class name to an existing `cellClass` / `headerClass` value
   *  (which can be a string, array, or function in AG Grid). Function-form
   *  is left untouched. */
  private appendClass(existing: string | string[] | undefined, add: string): string {
    if (!existing) return add;
    if (Array.isArray(existing)) return [...existing, add].join(' ');
    if (typeof existing === 'string') return `${existing} ${add}`;
    return add;
  }

  ngOnDestroy(): void {
    if (this.sub) this.sub.unsubscribe();
  }
}
